
import Vue from 'vue';
import axios from 'axios';
import EventService from '../services/EventService';
import DebugFeature from '../services/DebugFeature';
import {mapState} from 'vuex';
import store, {
  GetScopeFromKey,
  ConfSortByType, // TODO (ohtsuchi) 2025年以降 コードを削除
  ConfSortByValue, // TODO (ohtsuchi) 2025年以降 コードを削除
  ScopeKey,
} from '../store';
import moment from 'moment';
import Breadcrumbs from '@/components/breadcrumbs.vue';
import {TrainingCategory, TrainingCategoryDefault} from '@/components/types';
import type {User} from '@firebase/auth';

const baseURL = EventService.ProtoURL();
const apiClient = axios.create({
  baseURL: baseURL,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

apiClient.interceptors.request.use(config => {
  const companyID = store.state.current;
  if (companyID !== '') {
    config.headers['x-company-id'] = companyID;
  }
  return config;
});

export type APIData = {
  file: string;
  id: string;
  safetyEducationIndex: number;
  summary: string;
  year: string;
  tags: TrainingCategory[];
};

type Data = {
  items: APIData[];
  startData: string;
  isStart: boolean;
  page: number;
  allTags: TrainingCategory[];
  filterTag: string;
};

type Method = {
  jump(id: string): void;
  indexFormat(src: number): string;
  filterByTag(tag: string): void;
};

type Computed = {
  itemsSlice: APIData[];
  filteredItems: APIData[];
  canShowFilterTag: boolean;
  isNoSubGroup: boolean;
  user: User | undefined;
  currentScope: ScopeKey;
  noSubGroupStartYear: number;
  sortBy: ConfSortByType;
};

type Props = {
  year: string;
  main: string;
};

export default Vue.extend<Data, Method, Computed, Props>({
  name: 'tow-d-movies',
  components: {
    Breadcrumbs,
  },
  props: {
    year: {
      type: String,
      default: '',
    },
    main: {
      type: String,
      default: 'true',
    },
  },
  computed: {
    itemsSlice: function () {
      return getFilteredItems(this.filterTag, this.items).slice(
        (this.page - 1) * 12,
        this.page * 12
      );
    },
    filteredItems: function () {
      return getFilteredItems(this.filterTag, this.items);
    },
    canShowFilterTag(): boolean {
      return !this.main;
    },
    isNoSubGroup: function (): boolean {
      return (
        this.noSubGroupStartYear != 0 &&
        Number(this.year) >= this.noSubGroupStartYear
      );
    },
    ...mapState([
      'user',
      'currentScope',
      'noSubGroupStartYear',
      'sortBy', // TODO (ohtsuchi) 2025年以降 コードを削除
    ]),
  },
  methods: {
    jump(id: string) {
      this.$router.push(
        `/2d/${id}?year=${this.year}&scope=${GetScopeFromKey(
          this.currentScope
        )}&main=${this.main}`
      );
    },
    indexFormat(src: number): string {
      return EventService.getSafetyEducationIndexFormat(src);
    },
    filterByTag(tag: string) {
      this.page = 1;
      this.filterTag = tag;
    },
  },
  data: (): Data => ({
    items: [],
    startData: '',
    isStart: false,
    page: 1,
    allTags: [],
    filterTag: TrainingCategoryDefault,
  }),
  async mounted() {
    try {
      if (this.user) {
        const token = await this.user.getIdToken();
        var now = moment();
        if (DebugFeature.FeatureYear2023()) {
          now = moment('2023-12-31');
        }
        const y = await EventService.getYearInfo(token, Number(this.year));
        // const tmp = moment(y.data.start);
        // const m = moment({y: tmp.year(), M: tmp.month(), d: 1});
        const m = moment(y.data.dates.fiscalYearStart);
        const end = moment(y.data.dates.fiscalYearStart)
          .add(1, 'y')
          .subtract(1, 'ms');
        if (now.isAfter(end)) {
          this.startData = `の動画はすでに利用期限(${end.format(
            'YYYY-MM-DD'
          )})が過ぎてます 次年度の2D動画を視聴してください。`;
        } else if (now.isAfter(m)) {
          this.isStart = true;
        } else {
          this.startData = ` (${m.format('YYYY/MM/DD')}から利用開始できます。)`;
        }
        const {data} = await apiClient.get(
          `/2d/movies/?year=${this.year}&scope=${GetScopeFromKey(
            this.currentScope
          )}&main=${this.main}`,
          {
            headers: {Authorization: `Bearer ${token}`},
          }
        );
        // TODO (ohtsuchi) 2025年以降 コードを削除 --- ここから
        if ((this.sortBy as ConfSortByType) === ConfSortByValue.Changed) {
          const sortByIDChar = (a: {id: string}, b: {id: string}): number => {
            return a.id.localeCompare(b.id);
          };
          data.sort(sortByIDChar);
        }
        // TODO (ohtsuchi) 2025年以降 コードを削除 --- ここまで
        if (!this.main) {
          for (const training of data) {
            // サーバー側で Training の tags と Category を inner join した結果をフロント側で重複排除
            this.allTags = [
              ...new Map(
                [...this.allTags, ...training.tags].map(tag => [tag.key, tag]) // key で 重複排除
              ).values(),
            ];
          }
          this.allTags.sort((a: TrainingCategory, b: TrainingCategory) => {
            if (a.order < b.order) return -1;
            if (a.order > b.order) return 1;
            return 0;
          });
        }
        this.items = data;
      }
    } catch (e) {
      console.log(e);
    }
  },
});

function getFilteredItems(filterTag: string, items: APIData[]): APIData[] {
  return filterTag === TrainingCategoryDefault
    ? items
    : items.filter(t => {
        return t.tags.some(tag => tag.key === filterTag);
      });
}
